/**
 * Utilitaires de sous-titres - Version navigateur
 * Module conçu pour être utilisé dans React sans dépendances Node.js
 */

// Convertir hex en format de couleur ASS (AABBGGRR)
export function hexToAssColor(hex, alpha = 1) {
    // Supprimer le # si présent
    hex = hex.replace('#', '');

    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    // Convertir en RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const a = Math.round((1 - alpha) * 255);

    // Format ASS: &HAABBGGRR (AA=alpha, BB=blue, GG=green, RR=red)
    return `&H${a.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${r.toString(16).padStart(2, '0')}`;
}

// Convertir rgba en format de couleur ASS
export function rgbaToAssColor(rgba) {
    const match = rgba.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
    if (!match) return '&H7F000000'; // Défaut : fond noir semi-transparent

    const r = parseInt(match[1]);
    const g = parseInt(match[2]);
    const b = parseInt(match[3]);
    const alpha = parseFloat(match[4]);
    const a = Math.round((1 - alpha) * 255);

    // Format ASS: &HAABBGGRR
    return `&H${a.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${r.toString(16).padStart(2, '0')}`;
}

// Convertir couleur ASS en rgba pour CSS
export function assColorToRgba(assColor) {
    // Format attendu: &HAABBGGRR
    if (!assColor || !assColor.startsWith('&H')) {
        return 'rgba(255, 255, 255, 1)'; // Valeur par défaut
    }

    // Extraire les composantes
    const colorValue = assColor.substring(2);
    const alphaHex = colorValue.substring(0, 2);
    const blueHex = colorValue.substring(2, 4);
    const greenHex = colorValue.substring(4, 6);
    const redHex = colorValue.substring(6, 8);

    // Convertir en décimal
    const alpha = 1 - (parseInt(alphaHex, 16) / 255);
    const red = parseInt(redHex, 16);
    const green = parseInt(greenHex, 16);
    const blue = parseInt(blueHex, 16);

    return `rgba(${red}, ${green}, ${blue}, ${alpha.toFixed(2)})`;
}

// Convertir couleur ASS en hex pour l'éditeur
export function assColorToHex(assColor) {
    if (!assColor || !assColor.startsWith('&H')) return '#FFFFFF';

    const colorValue = assColor.substring(2);
    const blueHex = colorValue.substring(2, 4);
    const greenHex = colorValue.substring(4, 6);
    const redHex = colorValue.substring(6, 8);

    return `#${redHex}${greenHex}${blueHex}`;
}

// Convertir position textuelle en alignement ASS numérique (1-9)
export function getAssAlignment(alignment, position) {
    /*
    Valeurs d'alignement ASS:
    7 8 9 (haut)
    4 5 6 (milieu)
    1 2 3 (bas)
    
    Où 1,4,7 = gauche
        2,5,8 = centre
        3,6,9 = droite
    */
    if (position === 'top') {
        if (alignment === 'left') return 7;
        else if (alignment === 'center') return 8;
        else if (alignment === 'right') return 9;
    } else if (position === 'middle') {
        if (alignment === 'left') return 4;
        else if (alignment === 'center') return 5;
        else if (alignment === 'right') return 6;
    } else { // bottom
        if (alignment === 'left') return 1;
        else if (alignment === 'center') return 2;
        else if (alignment === 'right') return 3;
    }

    return 2; // Default: bottom-center
}

// Convertir alignement ASS en position textuelle pour l'UI
export function getPositionFromAssAlignment(alignment) {
    let position = 'bottom';
    let align = 'center';

    if (alignment >= 7) {
        position = 'top';
    } else if (alignment >= 4) {
        position = 'middle';
    }

    if (alignment === 1 || alignment === 4 || alignment === 7) {
        align = 'left';
    } else if (alignment === 3 || alignment === 6 || alignment === 9) {
        align = 'right';
    }

    return { position, alignment: align };
}

// Détecte les chevauchements et attribue des niveaux de style
export function processSubtitlesWithOverlap(subtitles, settings) {
    if (!subtitles || subtitles.length === 0) {
        return [];
    }

    // Si la gestion avancée des chevauchements est désactivée
    if (settings && settings.overlapHandling !== 'advanced') {
        return subtitles.map(subtitle => ({
            ...subtitle,
            styleLevel: 'main'
        }));
    }

    // Trier par temps de début
    const sortedSubtitles = [...subtitles].sort((a, b) => a.start - b.start);

    // Structure pour suivre les lignes occupées
    const occupiedLines = {
        main: [],     // ligne principale
        bottom2: [],  // ligne légèrement au-dessus
        bottom3: []   // ligne encore plus haut
    };

    // Attribuer un style à chaque sous-titre
    const processedSubtitles = sortedSubtitles.map(subtitle => {
        // Vérifier la disponibilité de chaque position
        const positions = ['main', 'bottom2', 'bottom3'];

        // Trouver la première position disponible
        let selectedPosition = null;

        for (const pos of positions) {
            const isOccupied = occupiedLines[pos].some(interval => {
                return (subtitle.start < interval.end && subtitle.end > interval.start);
            });

            if (!isOccupied) {
                selectedPosition = pos;
                break;
            }
        }

        // Si toutes les positions sont occupées, utiliser quand même la dernière
        if (selectedPosition === null) {
            selectedPosition = positions[positions.length - 1];
        }

        // Marquer cette position comme occupée pendant la durée du sous-titre
        occupiedLines[selectedPosition].push({
            start: subtitle.start,
            end: subtitle.end
        });

        // Retourner le sous-titre avec son niveau de style
        return {
            ...subtitle,
            styleLevel: selectedPosition
        };
    });

    return processedSubtitles;
}

// Créer le contenu d'un fichier ASS complet à partir des données JSON
export function createAssContent(subtitles, settings = {}) {
    // Traiter les sous-titres pour gérer les chevauchements
    const processedSubtitles = processSubtitlesWithOverlap(subtitles, settings);

    // En-tête du fichier ASS
    let content = `[Script Info]
; Script generated by ASS Subtitle Exporter
Title: Video Subtitles
ScriptType: v4.00+
PlayResX: 1920
PlayResY: 1080
Collisions: Normal
WrapStyle: 2
ScaledBorderAndShadow: yes

[V4+ Styles]
Format: Name, Fontname, Fontsize, PrimaryColour, SecondaryColour, OutlineColour, BackColour, Bold, Italic, Underline, StrikeOut, ScaleX, ScaleY, Spacing, Angle, BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV, Encoding
`;

    // Styles par défaut si non fournis
    const defaultSettings = {
        fontName: "Arial",
        fontSize: 24,
        primaryColor: "&H00FFFFFF",
        backColor: "&H80000000",
        bold: 0,
        italic: 0,
        borderStyle: 4,
        outline: 2,
        shadow: 1,
        alignment: 2,
        marginL: 10,
        marginR: 10,
        marginV: 10
    };

    const mergedSettings = { ...defaultSettings, ...settings };

    // Créer les styles pour chaque niveau (gestion des chevauchements)
    const styles = {
        main: {
            name: "Main",
            fontName: mergedSettings.fontName,
            fontSize: mergedSettings.fontSize,
            primaryColor: mergedSettings.primaryColor,
            secondaryColor: mergedSettings.primaryColor,
            outlineColor: "&H00000000", // Noir
            backColor: mergedSettings.backColor,
            bold: mergedSettings.bold,
            italic: mergedSettings.italic,
            underline: 0,
            strikeout: 0,
            scaleX: 100,
            scaleY: 100,
            spacing: 0,
            angle: 0,
            borderStyle: mergedSettings.borderStyle,
            outline: mergedSettings.outline,
            shadow: mergedSettings.shadow,
            alignment: mergedSettings.alignment,
            marginL: mergedSettings.marginL,
            marginR: mergedSettings.marginR,
            marginV: mergedSettings.marginV,
            encoding: 1,
        },
        bottom2: {
            name: "Bottom2",
            fontName: mergedSettings.fontName,
            fontSize: mergedSettings.fontSize,
            primaryColor: mergedSettings.primaryColor,
            secondaryColor: mergedSettings.primaryColor,
            outlineColor: "&H00000000",
            backColor: mergedSettings.backColor,
            bold: mergedSettings.bold,
            italic: mergedSettings.italic,
            underline: 0,
            strikeout: 0,
            scaleX: 100,
            scaleY: 100,
            spacing: 0,
            angle: 0,
            borderStyle: mergedSettings.borderStyle,
            outline: mergedSettings.outline,
            shadow: mergedSettings.shadow,
            alignment: mergedSettings.alignment,
            marginL: mergedSettings.marginL,
            marginR: mergedSettings.marginR,
            marginV: mergedSettings.marginV + 40, // Décalage pour éviter les chevauchements
            encoding: 1,
        },
        bottom3: {
            name: "Bottom3",
            fontName: mergedSettings.fontName,
            fontSize: mergedSettings.fontSize,
            primaryColor: mergedSettings.primaryColor,
            secondaryColor: mergedSettings.primaryColor,
            outlineColor: "&H00000000",
            backColor: mergedSettings.backColor,
            bold: mergedSettings.bold,
            italic: mergedSettings.italic,
            underline: 0,
            strikeout: 0,
            scaleX: 100,
            scaleY: 100,
            spacing: 0,
            angle: 0,
            borderStyle: mergedSettings.borderStyle,
            outline: mergedSettings.outline,
            shadow: mergedSettings.shadow,
            alignment: mergedSettings.alignment,
            marginL: mergedSettings.marginL,
            marginR: mergedSettings.marginR,
            marginV: mergedSettings.marginV + 80, // Décalage plus grand pour le troisième niveau
            encoding: 1,
        }
    };

    // Ajouter les définitions de style
    Object.values(styles).forEach(style => {
        content += `Style: ${style.name},${style.fontName},${style.fontSize},${style.primaryColor},${style.secondaryColor},${style.outlineColor},${style.backColor},${style.bold},${style.italic},${style.underline},${style.strikeout},${style.scaleX},${style.scaleY},${style.spacing},${style.angle},${style.borderStyle},${style.outline},${style.shadow},${style.alignment},${style.marginL},${style.marginR},${style.marginV},${style.encoding}\n`;
    });

    // Section des événements (sous-titres)
    content += `
[Events]
Format: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text
`;

    // Formatage du temps pour ASS (H:MM:SS.CS)
    function formatAssTime(seconds) {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);
        const cs = Math.floor((seconds * 100) % 100);

        return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}.${cs.toString().padStart(2, '0')}`;
    }

    // Ajouter les sous-titres
    processedSubtitles.forEach(subtitle => {
        const start = formatAssTime(subtitle.start);
        const end = formatAssTime(subtitle.end);
        const style = subtitle.styleLevel === 'bottom2' ? 'Bottom2' :
            subtitle.styleLevel === 'bottom3' ? 'Bottom3' : 'Main';

        // Gérer les styles personnalisés par sous-titre
        let extraStyle = '';
        if (subtitle.customPrimaryColor) {
            extraStyle += `\\c${subtitle.customPrimaryColor}`;
        }
        if (subtitle.customBackColor && mergedSettings.borderStyle === 4) {
            extraStyle += `\\4c${subtitle.customBackColor}`;
        }
        if (subtitle.bold !== undefined) {
            extraStyle += `\\b${subtitle.bold}`;
        }
        if (subtitle.italic !== undefined) {
            extraStyle += `\\i${subtitle.italic}`;
        }
        if (subtitle.customAlignment) {
            extraStyle += `\\an${subtitle.customAlignment}`;
        }

        // Préparer le texte avec retours à la ligne
        const text = subtitle.text.replace(/\n/g, '\\N');

        // Ajouter les styles si présents
        const styledText = extraStyle ? `{${extraStyle}}${text}` : text;

        content += `Dialogue: 0,${start},${end},${style},,0,0,0,,${styledText}\n`;
    });

    return content;
}

// Migrer d'anciennes données vers le nouveau format
export function migrateToNewFormat(oldData) {
    const { settings = {}, subtitles = [] } = oldData;

    // Migrer les paramètres globaux
    const newSettings = {
        enabled: settings.enabled || true,
        fontName: settings.font || 'Arial',
        fontSize: settings.fontSize || 24,
        primaryColor: settings.assTextColor || hexToAssColor(settings.color || '#FFFFFF'),
        backColor: settings.assBackColor || (settings.backgroundColor
            ? rgbaToAssColor(settings.backgroundColor)
            : '&H80000000'),
        borderStyle: settings.borderStyle || 4,
        bold: settings.bold ? -1 : 0, // Format ASS: -1=true, 0=false
        italic: settings.italic ? -1 : 0,
        outline: settings.outline || 2,
        shadow: settings.shadow || 1,
        alignment: settings.assAlignment || getAssAlignment(
            settings.alignment || 'center',
            settings.position || 'bottom'
        ),
        marginL: settings.marginL || 10,
        marginR: settings.marginR || 10,
        marginV: settings.marginV || 10,
        overlapHandling: settings.overlapHandling || 'advanced',
        showBackground: settings.showBackground !== undefined ? settings.showBackground : true
    };

    // Migrer les sous-titres
    const newSubtitles = subtitles.map(subtitle => {
        const newSubtitle = {
            ...subtitle,
            // Convertir directement les propriétés qui ne changent pas
            start: subtitle.start,
            end: subtitle.end,
            text: subtitle.text,
            speaker: subtitle.speaker || 'SPEAKER_0',

            // Propriétés ASS directes
            customPrimaryColor: subtitle.assTextColor || (subtitle.customColor ? hexToAssColor(subtitle.customColor) : undefined),
            customAlignment: subtitle.assAlignment || (subtitle.alignment ?
                getAssAlignment(subtitle.alignment, settings.position || 'bottom') : undefined),

            // Propriétés de style
            bold: subtitle.bold !== null && subtitle.bold !== undefined ?
                (subtitle.bold ? -1 : 0) : undefined,
            italic: subtitle.italic !== null && subtitle.italic !== undefined ?
                (subtitle.italic ? -1 : 0) : undefined
        };

        // Supprimer les anciennes propriétés pour éviter la duplication
        delete newSubtitle.customColor;
        delete newSubtitle.alignment;
        delete newSubtitle.assTextColor;
        delete newSubtitle.assAlignment;

        return newSubtitle;
    });

    return {
        settings: newSettings,
        subtitles: newSubtitles
    };
}